import { useCallback } from "react"
import { useUpdateFurnitureCustomization } from "hooks/api/quotation/quotations.hook"
import type {
	TFurniture,
	TFurnitureCustomization,
	TUpdateFurnitureCustomizationResponse,
} from "types/elevationReferences.type"

/**
 * The customization step every "add an object" flow chains onto its create (REQ-413/414), shared
 * by the cabinet, Other and countertop modes so the three cannot drift apart.
 *
 * The customization endpoint is per-object, so it cannot exist before the object does: creation is
 * `POST …/furniture/` first, then — and only when the panel's section actually holds something —
 * ONE `PUT …/furniture/{newId}/customization/` against the version the previous step returned. A
 * collapsed or half-filled section yields no payload and therefore no call at all, which is what
 * keeps "add a customization at creation" to exactly one extra request and no more.
 *
 * The PUT is best-effort, on the same contract `useOtherCreate` applies to its name / fixed-price
 * PATCHes: past the create the object EXISTS, so abandoning the flow on a rejection here would
 * leave an object nothing points at. The failure is surfaced as a toast, the best-known furniture
 * is returned, and the caller still caches it and hands the user over to its own sidebar — where
 * the same two fields are waiting to be retyped.
 */
export const useApplyCustomization = (formatError: (error: unknown) => void) => {
	const { mutateAsync: updateFurnitureCustomizationAsync } = useUpdateFurnitureCustomization()

	return useCallback(
		async (
			quotationId: number,
			furniture: TFurniture,
			customization: TFurnitureCustomization | null,
		): Promise<TFurniture> => {
			if (customization === null) return furniture

			try {
				const response = await updateFurnitureCustomizationAsync({
					quotationId,
					furnitureId: furniture.id,
					body: {
						version: furniture.version,
						note: customization.note,
						price_adjustment: customization.price_adjustment,
					},
				})
				const data = response?.data as TUpdateFurnitureCustomizationResponse | undefined
				return {
					...furniture,
					version: typeof data?.version === "number" ? data.version : furniture.version + 1,
					customization: {
						note: data?.note ?? customization.note,
						price_adjustment:
							data?.price_adjustment !== undefined ? data.price_adjustment : customization.price_adjustment,
					},
				}
			} catch (error) {
				formatError(error)
				return furniture
			}
		},
		[updateFurnitureCustomizationAsync, formatError],
	)
}
